import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, TrendingUp, User as UserIcon, Shield } from 'lucide-react';
import { useGetUserDetailQuery, useChangeUserStatusMutation } from './usersApi';
import Toggle from '../../../ui-components/Toggle';
import { toast } from 'react-toastify';
import { formatTimestampToDateTime } from '../../../utils/common';
import { usePageHeaderContext } from '../../../context/PageHeaderContext';
import type { UserDetail, UserActivityEvent } from './model';
import StatusTag from '../../../ui-components/StatusTag';

// Local Status Badge for better matching the screenshot if needed
const LocalStatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const isActive = status?.toLowerCase() === 'active';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
        isActive
          ? 'bg-success-100 text-success-700 border border-success-200 shadow-sm'
          : 'bg-error-100 text-error-700 border border-error-200 shadow-sm'
      }`}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-success-500' : 'bg-error-500'}`}
      />
      {status}
    </span>
  );
};

const ActivityList: React.FC<{
  data: UserActivityEvent[];
  emptyMessage: string;
  type: 'created' | 'joined';
}> = ({ data, emptyMessage, type }) => {
  if (data.length === 0) {
    return (
      <div className="p-12 text-center text-neutral-500 italic bg-neutral-50/50 dark:bg-neutral-800/20 rounded-xl border border-dashed border-neutral-200 dark:border-neutral-700">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto shadow-sm border border-neutral-200 dark:border-neutral-800 rounded-xl">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="bg-neutral-50 dark:bg-neutral-800/50 text-neutral-500 dark:text-neutral-400 uppercase text-[10px] font-bold tracking-wider">
            <th className="px-6 py-4">Event ID</th>
            <th className="px-6 py-4">Event Name</th>
            {type === 'joined' && <th className="px-6 py-4">Host Name</th>}
            {type === 'joined' && <th className="px-6 py-4">RSVP Status</th>}
            <th className="px-6 py-4">Start Date</th>
            {type === 'created' && <th className="px-6 py-4">Expiry Date</th>}
            <th className="px-6 py-4">Status</th>
            {type === 'created' && <th className="px-6 py-4">Created Date</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
          {data.map((event) => (
            <tr
              key={event.eventId}
              className="hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors"
            >
              <td className="px-6 py-4 font-medium text-neutral-900 dark:text-white">
                {event.eventId}
              </td>
              <td className="px-6 py-4 font-medium">
                <Link
                  to={`/events/view/${(event.id || event.eventId).toString().replace('EVT-', '')}`}
                  className="text-brand-600 hover:text-brand-700 hover:underline transition-colors"
                >
                  {event.eventName}
                </Link>
              </td>
              {type === 'joined' && (
                <td className="px-6 py-4 text-neutral-600 dark:text-neutral-400">
                  {event.hostName}
                </td>
              )}
              {type === 'joined' && (
                <td className="px-6 py-4">
                  <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase bg-success-50 text-success-700 border border-success-100">
                    <span className="w-1 h-1 rounded-full bg-success-500" />
                    {event.rsvpStatus}
                  </span>
                </td>
              )}
              <td className="px-6 py-4 text-neutral-600 dark:text-neutral-400">
                {formatTimestampToDateTime(event.startDate)}
              </td>
              {type === 'created' && (
                <td className="px-6 py-4 text-neutral-600 dark:text-neutral-400">
                  {formatTimestampToDateTime(event.expiryDate)}
                </td>
              )}
              <td className="px-6 py-4">
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                    event.status.toLowerCase() === 'active'
                      ? 'bg-success-100 text-success-700'
                      : 'bg-neutral-100 text-neutral-700'
                  }`}
                >
                  <span
                    className={`w-1 h-1 rounded-full ${event.status.toLowerCase() === 'active' ? 'bg-success-500' : 'bg-neutral-400'}`}
                  />
                  {event.status}
                </span>
              </td>
              {type === 'created' && (
                <td className="px-6 py-4 text-neutral-600 dark:text-neutral-400">
                  {formatTimestampToDateTime(event.createdDate)}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const UserDetailPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'basic' | 'created' | 'joined'>(
    'basic'
  );

  const { setTitle, setSubtitle, setBreadcrumbs, setActions } =
    usePageHeaderContext();

  const {
    data: basicResponse,
    isLoading: isBasicLoading,
    isError: isBasicError,
  } = useGetUserDetailQuery({ userId: userId || '' });

  const { data: createdResponse, isLoading: isCreatedLoading } =
    useGetUserDetailQuery(
      { userId: userId || '', param: 'created_event' },
      { skip: activeTab !== 'created' }
    );

  const { data: joinedResponse, isLoading: isJoinedLoading } =
    useGetUserDetailQuery(
      { userId: userId || '', param: 'joined_event' },
      { skip: activeTab !== 'joined' }
    );

  const [changeStatus, { isLoading: isStatusUpdating }] =
    useChangeUserStatusMutation();

  const user = basicResponse?.data as UserDetail;
  const createdEvents = (createdResponse?.data as UserActivityEvent[]) || [];
  const joinedEvents = (joinedResponse?.data as UserActivityEvent[]) || [];

  useEffect(() => {
    setTitle('User Details');
    setSubtitle('View complete user information and activity');
    setActions(
      <button
        onClick={() => navigate('/users/list')}
        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors shadow-sm"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Users
      </button>
    );

    const userName = user?.basicInfo?.name || 'Loading...';
    setBreadcrumbs([
      { label: 'User Management', to: '/users/list' },
      { label: userName, current: true },
    ]);

    return () => {
      setTitle(undefined);
      setSubtitle(undefined);
      setActions(undefined);
      setBreadcrumbs(undefined);
    };
  }, [setTitle, setSubtitle, setActions, setBreadcrumbs, navigate, user]);

  const handleStatusToggle = async () => {
    if (!user) return;
    const nextStatus =
      user.basicInfo.status.toLowerCase() === 'active' ? 'inactive' : 'active';
    try {
      await changeStatus({
        id: userId || '',
        status: nextStatus,
      }).unwrap();
    } catch (error: any) {
      toast.error(error?.data?.settings?.message || 'Failed to update status');
    }
  };

  if (isBasicLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500" />
      </div>
    );
  }

  if (isBasicError || !user) {
    return (
      <div className="p-8 text-center">
        <h2 className="text-xl font-semibold text-neutral-800 dark:text-neutral-200">
          User not found
        </h2>
        <button
          onClick={() => navigate('/users/list')}
          className="mt-4 text-brand-600 hover:underline inline-flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Users
        </button>
      </div>
    );
  }

  const stats = [
    {
      id: 'total-created',
      label: 'Total Events Created',
      value: user.summary.totalCreated,
      icon: TrendingUp,
    },
    {
      id: 'total-joined',
      label: 'Total Events Joined',
      value: user.summary.totalJoined,
      icon: TrendingUp,
    },
    {
      id: 'active-events',
      label: 'Current Active Events',
      value: user.summary.activeEvents,
      icon: TrendingUp,
    },
    {
      id: 'expired-events',
      label: 'Expired Events',
      value: user.summary.expiredEvents,
      icon: TrendingUp,
    },
  ];

  return (
    <div className="p-6  mx-auto">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats.map((stat) => (
          <div
            key={stat.id}
            className="bg-white dark:bg-neutral-900 p-6 rounded-xl border border-neutral-200 dark:border-neutral-800 shadow-sm relative overflow-hidden group"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-3xl font-bold text-neutral-900 dark:text-white">
                {stat.value}
              </span>
              <div className="p-2 bg-brand-50 dark:bg-brand-900/20 rounded-lg text-brand-600 dark:text-brand-400">
                <stat.icon className="w-5 h-5" />
              </div>
            </div>
            <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
              {stat.label}
            </p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="bg-neutral-100/50 dark:bg-neutral-800/30 p-1 rounded-xl flex gap-1 mb-6">
        {[
          { id: 'basic', label: 'Basic Information' },
          { id: 'created', label: 'Created Events' },
          { id: 'joined', label: 'Joined Events' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
              activeTab === tab.id
                ? 'bg-white dark:bg-neutral-900 text-brand-600 shadow-sm ring-1 ring-black/5'
                : 'text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 hover:dark:text-neutral-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 shadow-sm overflow-hidden">
        {activeTab === 'basic' && (
          <div className="p-8">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 bg-brand-50 dark:bg-brand-900/20 rounded-xl flex items-center justify-center text-brand-600">
                <UserIcon className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-neutral-900 dark:text-white">
                  Basic Information
                </h3>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  User account details and status
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8 mb-10">
              <div className="space-y-2">
                <span className="text-xs font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider block">
                  Name
                </span>
                <div className="px-4 py-3 bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 rounded-lg text-neutral-700 dark:text-neutral-300">
                  {user.basicInfo.name}
                </div>
              </div>
              <div className="space-y-2">
                <span className="text-xs font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider block">
                  Email
                </span>
                <div className="px-4 py-3 bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 rounded-lg text-neutral-700 dark:text-neutral-300">
                  {user.basicInfo.email || '—'}
                </div>
              </div>
              <div className="space-y-2">
                <span className="text-xs font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider block">
                  Created Date
                </span>
                <div className="px-4 py-3 bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 rounded-lg text-neutral-700 dark:text-neutral-300">
                  {formatTimestampToDateTime(user.basicInfo.createdDate)}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <span className="text-xs font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider block">
                Account Status
              </span>
              <div className="flex items-center gap-4">
                <Toggle
                  checked={user.basicInfo.status.toLowerCase() === 'active'}
                  isLoading={isStatusUpdating}
                  onChange={handleStatusToggle}
                />
                <StatusTag value={user.basicInfo.status} />
                <span className="text-xs text-neutral-500 dark:text-neutral-400 ml-2">
                  User can log in and access the platform
                </span>
              </div>
            </div>

            <div className="mt-10 p-4 bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 rounded-xl flex gap-3 text-neutral-600 dark:text-neutral-400">
              <Shield className="w-5 h-5 shrink-0 opacity-50" />
              <p className="text-sm">
                <span className="font-bold">Note:</span> Admin cannot edit user
                name or email. Admin cannot delete users from this screen.
                Status toggle controls user login access.
              </p>
            </div>
          </div>
        )}

        {activeTab === 'created' && (
          <div className="p-8">
            {isCreatedLoading ? (
              <div className="flex justify-center p-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500" />
              </div>
            ) : (
              <ActivityList
                data={createdEvents}
                emptyMessage="No events created yet."
                type="created"
              />
            )}
          </div>
        )}

        {activeTab === 'joined' && (
          <div className="p-8">
            {isJoinedLoading ? (
              <div className="flex justify-center p-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500" />
              </div>
            ) : (
              <ActivityList
                data={joinedEvents}
                emptyMessage="No events joined yet."
                type="joined"
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDetailPage;
